import React, { useState, useEffect } from "react";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// Rows = pop-up OFFERS. Columns = your real segments (player_types from API).
// A checked cell = that offer is EXCLUDED for that segment (the spec's "— never").
const key = (popupId, segId) => `${popupId}|${segId}`;

const SegmentationModal = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [offers, setOffers] = useState([]);          // [{id, name, ...}]
  const [segments, setSegments] = useState([]);      // [{id, player_type}]
  const [excluded, setExcluded] = useState(new Set()); // Set of "popupId|segId"

  useEffect(() => {
    if (!open) return;
    (async () => {
      setIsLoading(true);
      try {
        const off = await getAllData(endPoints.api.POPUP_OFFER_ALL);
        setOffers(off?.status === "success" ? (off.data || []) : (Array.isArray(off) ? off : []));
        const seg = await getAllData(endPoints.api.playertypes);
        setSegments(Array.isArray(seg) ? seg : (seg?.data || []));
        const ex = await getAllData(endPoints.api.POPUP_SEG_GET);
        if (ex?.status === "success" && Array.isArray(ex.data)) {
          const s = new Set();
          ex.data.forEach((r) => s.add(key(r.popupId ?? r.popup_id, r.playerTypeId ?? r.player_type_id)));
          setExcluded(s);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [open]);

  const toggle = (popupId, segId) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      const k = key(popupId, segId);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const save = () => {
    const exclusions = Array.from(excluded).map((k) => {
      const [popupId, segId] = k.split("|");
      return { popupId: Number(popupId), playerTypeId: Number(segId) };
    });
    setIsLoading(true);
    addData(endPoints.api.POPUP_SEG_SAVE, { exclusions })
      .then((response) => {
        setIsLoading(false);
        if (response?.data?.status === "success") { SuccesToast("Segmentation saved"); onClose(); }
        else ErrorToast(response?.data?.message || "Failed to save segmentation");
      })
      .catch((e) => { setIsLoading(false); console.error(e); ErrorToast("Failed to save segmentation"); });
  };

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
      <DialogTitle sx={{ pr: 6 }}>
        Pop-up Segmentation
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8, color: "#64607a" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Toaster />
        {isLoading && <Loader spinner={true} visible={isLoading} />}
        <p style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
          Tick a cell to <b>exclude</b> that pop-up from a segment. Empty = eligible.
        </p>
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold", minWidth: 220 }}>Pop-up offer</TableCell>
                {segments.map((s) => (
                  <TableCell key={s.id} align="center" style={{ fontWeight: "bold" }}>{s.player_type}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {offers.length === 0 && (
                <TableRow><TableCell colSpan={segments.length + 1} style={{ color: "#999" }}>No pop-up offers yet — create one first.</TableCell></TableRow>
              )}
              {offers.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell>{o.name} <span style={{ color: "#999", fontSize: 11 }}>({o.template})</span></TableCell>
                  {segments.map((s) => (
                    <TableCell key={s.id} align="center">
                      <Checkbox size="small" checked={excluded.has(key(o.id, s.id))} onChange={() => toggle(o.id, s.id)} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SegmentationModal;
